import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Button,
  Menu,
  MenuItem,
  TextField,
  Box
} from '@mui/material';
import axios from 'utils/axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const RoleClaimListEdit = () => {
  const { t } = useTranslation();
  const { roleId } = useParams();
  const navigate = useNavigate();

  const [resolved, setResolved] = useState({
    model: null,
    claimGroups: null,
    claims: [],
  });

  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [roleResponse, claimGroupsResponse, claimsResponse] = await Promise.all([
          axios.get(`users/role/${roleId}`),
          axios.get(`users/role/${roleId}/claimGroups`),
          axios.get(`users/role/${roleId}/claims`)
        ]);

        setResolved({
          model: roleResponse.data,
          claimGroups: claimGroupsResponse.data,
          claims: Array.isArray(claimsResponse.data.items) ? claimsResponse.data.items : [],
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(t('fetch_error'));
      }
    };

    fetchData();
  }, [roleId, t]);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleEditClick = () => {
    navigate(`/home/role/edit/${roleId}`);
    handleMenuClose();
  };

  const handleActivateClick = async () => {
    try {
      await axios.put(`users/role/${roleId}/activate`);
      setResolved((prev) => ({
        ...prev,
        model: { ...prev.model, disabled: false },
      }));
      toast.success(t('role_activated'));
    } catch (error) {
      toast.error(t('activation_failed'));
    }
    handleMenuClose();
  };

  const handleDeactivateClick = async () => {
    try {
      await axios.put(`users/role/${roleId}/deactivate`);
      setResolved((prev) => ({
        ...prev,
        model: { ...prev.model, disabled: true },
      }));
      toast.success(t('role_deactivated'));
    } catch (error) {
      toast.error(t('deactivation_failed'));
    }
    handleMenuClose();
  };

  const handleClaimChange = (claimId, checked) => {
    setResolved((prev) => {
      const updatedClaims = checked
        ? [...prev.claims, { claimId, roleId }]
        : prev.claims.filter((claim) => claim.claimId !== claimId);
      return { ...prev, claims: updatedClaims };
    });
  };

  const handleSaveClaims = async () => {
    try {
      await axios.put(`users/role/${roleId}/claims`, {
        roleId,
        actions: resolved.claims.map((claim) => claim.claimId),
      });
      toast.success(t('claims_saved'));
      navigate('/roles');
    } catch (error) {
      toast.error(t('save_failed'));
    }
  };

  const isClaimSelected = (claimId) =>
    resolved.claims.some((claim) => claim.claimId === claimId);

  if (!resolved.model) return null;

  return (
    <Card sx={{ p: 3 }}>
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label={t('name')}
              value={resolved.model.name}
              variant="outlined"
              fullWidth
              disabled
            />
          </Grid>
  
          <Grid item xs={12}>
            <TextField
              label={t('code')}
              value={resolved.model.code}
              variant="outlined"
              fullWidth
              disabled
            />
          </Grid>
  
          {resolved.claimGroups?.items?.map((group) => (
            <Grid item xs={12} key={group.id}>
              {group.claimTypes?.items?.map((claimType) => (
                <Box key={claimType.id} mt={3}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    {claimType.name}
                  </Typography>
  
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    {claimType.claims?.items?.map((claim) => (
                      <Grid item key={claim.id} xs="auto">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isClaimSelected(claim.id)}
                              onChange={(e) =>
                                handleClaimChange(claim.id, e.target.checked)
                              }
                            />
                          }
                          label={t(claim.name)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Grid>
          ))}
  
          <Grid item xs={12} mt={4}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={() => navigate('/roles')}>
                {t('cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveClaims}
              >
                {t('save')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
  
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick}>{t('edit')}</MenuItem>
        {resolved.model.disabled ? (
          <MenuItem onClick={handleActivateClick}>{t('activate')}</MenuItem>
        ) : (
          <MenuItem onClick={handleDeactivateClick}>{t('deactivate')}</MenuItem>
        )}
      </Menu>
    </Card>
  );
  
};

export default RoleClaimListEdit;
